import { isFutureVisitAtIso } from "@/components/handshake/visitAt";
import type { HandshakePendingConfirmContext } from "./BookingSellerHandshakeActions";
import type { BookingListRow } from "./bookingViewModel";
import {
  showPartnerManualReceiptAmount,
  showPartnerReceiptUpload,
} from "./sellerBookingRules";
import type {
  BookingActionCategory,
  BookingLifecycleStatus,
  HandshakeSessionStatus,
} from "@/types";

/**
 * Per-card action rules. Counting and filtering by bucket happen server-side
 * (`GET /bookings/attention`, `GET /bookings/page?action=`) with the same rules.
 */
export const BOOKING_ACTION_CATEGORIES: readonly BookingActionCategory[] = [
  "CONFIRMATION",
  "RECEIPT",
  "COMPLETION",
];

/** Live handshake request waiting on the partner (booking + session both open). */
export function isNewPartnerBooking(
  status: BookingLifecycleStatus,
  handshakeStatus: HandshakeSessionStatus | null,
): boolean {
  return status === "PENDING_HANDSHAKE" && handshakeStatus === "PENDING";
}

export function needsConfirmation(row: BookingListRow): boolean {
  return isNewPartnerBooking(row.status, row.handshakeStatus);
}

/**
 * Session context for confirming a pending booking, read from the row so list
 * cards skip the per-card session fetch. Undefined when the row lacks the slot
 * or the activity's availability mode (activity not loaded): fetch instead.
 */
export function pendingConfirmContext(
  row: BookingListRow,
): HandshakePendingConfirmContext | undefined {
  if (!row.timeSlot || !row.activityAvailabilityMode) return undefined;
  return {
    visitAt: row.visitAt,
    timeSlot: row.timeSlot,
    activityRegionCode: row.activityRegionCode,
    availabilityMode: row.activityAvailabilityMode,
  };
}

export function needsReceiptAction(row: BookingListRow): boolean {
  return showPartnerReceiptUpload(row) || showPartnerManualReceiptAmount(row);
}

export function needsCompletion(row: BookingListRow): boolean {
  if (row.status !== "ACCEPTED") {
    return false;
  }
  if (isFutureVisitAtIso(row.visitAt)) {
    return false;
  }
  if (needsReceiptAction(row)) {
    return false;
  }
  return true;
}
